import { useCallback, useEffect, useMemo, useState } from 'react'
import { lowerCase } from 'lodash'
import { toJS } from 'mobx'

import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { ArticleClient } from 'api/clients/ArticleClient'
import { ProjectClient } from 'api/clients/ProjectClient'
import reduxStore from 'redux/store'
import * as Actions from 'redux/actions'
import { ArticleFlat, FileAccessGrant, Permission, RecipientsSelectorValueType } from 'types'
import ShareSheetStore from 'stores/ShareSheetStore'
import { useAuth } from 'hooks/UseAuth'
import { Value as RecipientsSelectorValue } from 'components/ShareSheet/RecipientSelector/Types'
import AccessGrantsClient, { FileWithIdAndType } from 'api/clients/AccessGrantsClient'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { reportError } from 'utils/error'
import { canEdit } from 'components/ShareSheet/utils'
import { useCurrentWorkspace } from './useCurrentWorkspace'
import { DesignClient } from 'api/clients/DesignClient'

const useShareSheetV2 = (file: FileWithIdAndType) => {
  const [loading, setLoading] = useState(true)
  const [userCanEdit, setUserCanEdit] = useState(false)

  const {
    asset,
    currentUserHighestAccess,
    currentUserInheritedAccessGrant,
    inheritedAccesses,
    invitedAccesses,
    publicAccess,
    inheritedPublicAccess,
    takeOwnership,
    transferOwnershipUser,
    setShareSheet,
    inviteMessage,
    recipients,
    selectedPermission,
    isSubmitting,
    inviteToWorkspace,
    showInviteToWorkspaceScreen,
    footerOpen,
    mainLayerEditors,
    reviewScreenTitle,
  } = ShareSheetStore

  const auth = useAuth()
  const client = useMemo(() => auth && file && AccessGrantsClient(file), [auth, file])
  const workspace = useCurrentWorkspace()

  useEffect(() => {
    setUserCanEdit(canEdit(currentUserHighestAccess))
  }, [currentUserHighestAccess])

  const resetShareSheet = () => {
    setShareSheet({
      asset: null,
      currentUserHighestAccess: undefined,
      currentUserInheritedAccessGrant: null,
      inheritedAccesses: [],
      invitedAccesses: [],
      publicAccess: undefined,
      inheritedPublicAccess: undefined,
      takeOwnership: false,
      transferOwnershipUser: null,
      inviteMessage: '',
      recipients: [],
      selectedPermission: Permission.Edit,
      isSubmitting: false,
      inviteToWorkspace: false,
      showInviteToWorkspaceScreen: false,
      footerOpen: true,
      mainLayerEditors: [],
      reviewScreenTitle: '',
    })
    setLoading(true)
  }

  const loadShareSheet = useCallback(
    async ({
      initialPermission,
      initialRecipients,
    }: {
      initialPermission?: Permission
      initialRecipients?: RecipientsSelectorValue[]
    }) => {
      if (!client) return

      const res = await client.newList()

      if (res.type === SUCCESS_RESPONSE_TYPE) {
        setLoading(false)
        setShareSheet({
          asset: res.data.asset,
          currentUserHighestAccess: res.data.current_user_highest_access,
          currentUserInheritedAccessGrant: res.data.current_user_inherited_access_grant,
          inheritedAccesses: res.data.inherited_accesses,
          invitedAccesses: res.data.main_accesses,
          publicAccess: res.data.public_access || undefined,
          inheritedPublicAccess: res.data.inherited_public_access || undefined,
          mainLayerEditors: res.data.main_layer_editors || [],
          selectedPermission: initialPermission || Permission.Edit,
          recipients: initialRecipients || [],
        })
      } else {
        showErrorMessageToast('Something went wrong 😢')
        reportError('Failed to load share sheet', {
          severity: 'error',
          metaData: { file },
          user: { id: auth?.id },
        })
      }
    },
    [client, setShareSheet, file, auth],
  )

  const removePublicAccess = useCallback(
    async (accessGrant: FileAccessGrant, onError: () => void) => {
      if (!client) return

      const res = await client.destroy(accessGrant)
      if (res.type === 'error' && res.status === 403 && !workspace?.allow_public_links) {
        showErrorMessageToast('Update your workspace settings to modify public sharing')
        return
      }
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        showToastMessage('Public sharing settings were successfully changed!', 'success')
        loadShareSheet({})
      } else {
        onError()
        showErrorMessageToast('Something went wrong 😢')
        reportError('Failed to remove public access', {
          severity: 'error',
          metaData: { file },
          user: { id: auth?.id },
        })
      }
    },
    [client, loadShareSheet, file, auth, workspace?.allow_public_links],
  )

  const createPublicAccess = useCallback(
    async (access: Permission, onError: () => void, password?: string | null) => {
      if (!client) return

      const res = await client.publish(access, password)
      if (res.type === 'error' && res.status === 403 && !workspace?.allow_public_links) {
        showErrorMessageToast('Update your workspace settings to modify public sharing')
        return
      }
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        loadShareSheet({})
        showToastMessage('Public sharing settings were successfully changed!', 'success')
      } else {
        onError()
        showErrorMessageToast('Something went wrong 😢')
        reportError('Failed to update public access', {
          severity: 'error',
          metaData: { file },
          user: { id: auth?.id },
        })
      }
    },
    [client, loadShareSheet, file, auth, workspace?.allow_public_links],
  )

  const onUpdateAccess = async (id: number, accessLevel: Permission) => {
    if (!client) return

    const res = await client.update(id, accessLevel)
    if (res && res.type === SUCCESS_RESPONSE_TYPE) {
      showToastMessage('Access was updated!', 'success')
      loadShareSheet({})
    } else {
      showErrorMessageToast('There was an error 😢')
      return null
    }
  }

  const transferOwnership = async ({ id, goBack }: { id: number; goBack: () => void }) => {
    const isArticle = asset?.type === 'article' || file.type === 'article'
    const isDesign = asset?.type === 'design' || file.type === 'design'

    let client;
    client = ProjectClient
    if (isArticle) client = ArticleClient
    if (isDesign) client = DesignClient
    
    const response = await client.transferOwnership(file.id, id)
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      reportError(new Error(`Error transferring ${asset?.type} ownership`), {
        severity: 'error',
        metaData: {
          fileId: file.id,
          response,
        },
      })
      showErrorMessageToast('An error occurred while transferring the ownership of this file. Please try again...')
      return
    }

    showToastMessage('Ownership transferred successfully! 🎉', 'success')
    if (isArticle) {
      const data = response.data as { article: ArticleFlat }
      reduxStore.dispatch(Actions.articleMetaUpsert(data.article))
    }
    loadShareSheet({})
    goBack()
  }

  const buildRecipients = () => {
    return recipients.map((recipient) => {
      if (recipient.type === RecipientsSelectorValueType.Email)
        return { id: undefined, email: recipient.value, type: recipient.type }

      return { id: recipient.value.id as number, type: recipient.type, email: undefined }
    })
  }

  const inviteUsers = async () => {
    if (!client) return
    if (!asset) return

    setShareSheet({ isSubmitting: true })

    const response = await client.create({
      assetId: asset.id,
      assetType: asset.type,
      access: selectedPermission,
      inviteMessage,
      inviteToWorkspace,
      recipients: buildRecipients(),
    })

    if (response.type === SUCCESS_RESPONSE_TYPE) {
      setShareSheet({ recipients: [], isSubmitting: false, showInviteToWorkspaceScreen: false })
      loadShareSheet({})
      showToastMessage('Successfully shared', 'success')
    } else {
      setShareSheet({ isSubmitting: false })
      showErrorMessageToast('There was an error 😢')
      reportError('Failed to create share sheet accesses', {
        severity: 'error',
        metaData: { recipients, file },
        user: { id: auth?.id },
      })
    }
  }

  const onRemoveAccess = async ({ access, hasParentFolder }: { access: FileAccessGrant; hasParentFolder: boolean }) => {
    if (!client) return

    const accessorType = lowerCase(access.accessor.type)

    const buildMessage = () => {
      if (!access.other_access) return 'Access removed.'

      if (hasParentFolder) {
        return accessorType === 'user'
          ? `Access removed. This user may still inherit access through a parent folder or being in an invited group.`
          : `Access removed. This ${
              accessorType === 'channel' ? 'group' : accessorType
            }  may still inherit access through a parent folder.`
      }

      return accessorType === 'user'
        ? `Access removed. This user may still inherit access through being in an invited group.`
        : 'Access removed.'
    }

    const res = await client.destroy(access)
    if (res && res.type === SUCCESS_RESPONSE_TYPE) {
      showToastMessage(buildMessage(), 'success')
      loadShareSheet({})
    } else {
      showErrorMessageToast('There was an error 😢')
      return null
    }
  }

  return {
    asset: toJS(asset),
    currentUserHighestAccess,
    currentUserInheritedAccessGrant,
    inheritedAccesses: toJS(inheritedAccesses),
    invitedAccesses: toJS(invitedAccesses),
    publicAccess: toJS(publicAccess),
    inheritedPublicAccess: toJS(inheritedPublicAccess),
    createPublicAccess,
    removePublicAccess,
    loadShareSheet,
    onRemoveAccess,
    onUpdateAccess,
    loading,
    transferOwnership,
    userCanEdit,
    resetShareSheet,
    takeOwnership,
    transferOwnershipUser: toJS(transferOwnershipUser),
    inviteMessage,
    recipients: toJS(recipients),
    selectedPermission,
    isSubmitting,
    setShareSheet,
    inviteUsers,
    inviteToWorkspace,
    showInviteToWorkspaceScreen,
    footerOpen,
    mainLayerEditors: toJS(mainLayerEditors),
    reviewScreenTitle,
  }
}

export default useShareSheetV2
